import { useEffect, useState, type FormEvent } from "react";

import { ApiError } from "../api/client.js";
import { Icon } from "../components/Icon.js";
import { useI18n } from "../i18n.js";
import { useSession } from "../session.js";

/**
 * Accesso, e primo avvio.
 *
 * Portata da `Login.qml`, non reinventata: logo Pergam, marchio LMSmini in
 * Rajdhani con la spaziatura larga, sottotitolo maiuscolo, e la scheda
 * centrata da 420 px. La regola del porting e' che questa schermata sia la
 * stessa dell'app desktop — chi passa dall'una all'altra non deve accorgersi
 * di aver cambiato programma.
 *
 * Un solo schermo per due cose, come nel QML: quando la tabella utenti e'
 * vuota chiede di creare l'amministratore invece di chiedere una password che
 * non esiste. **Non c'e' una password di default.**
 *
 * UNA DIFFERENZA DAL DESKTOP, voluta: la' sotto la scheda c'e' il pannello
 * con lo stato hardware dei cluster. Qui no — `hardware-probe.results` e'
 * `authenticated`, e quell'elenco (nomi degli impianti e apparati che
 * rispondono) davanti a chi non ha ancora fatto il login e' una mappa.
 */
export function Login({ mode }: { mode: "login" | "bootstrap" }) {
  const { t } = useI18n();
  const { login, bootstrap } = useSession();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const isBootstrap = mode === "bootstrap";

  // Cambiare modo azzera i campi: restare con una password digitata mentre lo
  // schermo cambia significato e' il modo per crearla dove non si voleva.
  useEffect(() => {
    setPassword("");
    setConfirm("");
    setError("");
  }, [mode]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setError("");

    if (isBootstrap) {
      // Le due verifiche stanno anche sul server. Qui servono per rispondere
      // subito, non per fidarsi del client.
      if (password.length < 8) return setError(t("login.bootstrapTooShort"));
      if (password !== confirm) return setError(t("login.bootstrapMismatch"));
    }

    setBusy(true);
    try {
      if (isBootstrap) await bootstrap(username.trim(), password, username.trim());
      else await login(username.trim(), password);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("login.authError"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login">
      <div className="login__panel">
        <img className="login__logo" src="/assets/pergam_logo.png" alt="Pergam Group" />
        <h1 className="brand brand--xl">
          LMS<span>mini</span>
        </h1>
        <p className="login__subtitle">{t("login.supervisorySystem")}</p>

        <form className="card login__card" onSubmit={submit}>
          <h2 className="login__cardTitle">
            {isBootstrap ? t("login.bootstrapTitle") : t("login.operatorAccess")}
          </h2>
          {isBootstrap && <p className="login__hint">{t("login.bootstrapHint")}</p>}

          {error && (
            <p className="alert" role="alert">
              <Icon name="alert-circle" size={16} />
              <span>{error}</span>
            </p>
          )}

          <label className="field">
            <span className="field__label">{t("login.username")}</span>
            <input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              autoFocus
              required
            />
          </label>

          <label className="field">
            <span className="field__label">{t("login.password")}</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={isBootstrap ? "new-password" : "current-password"}
              placeholder="••••••••"
              required
            />
          </label>

          {isBootstrap && (
            <label className="field">
              <span className="field__label">{t("login.confirmPassword")}</span>
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                placeholder="••••••••"
                required
              />
            </label>
          )}

          <button className="btn btn--primary btn--wide" type="submit" disabled={busy || !username || !password}>
            <Icon name="log-in" size={16} />
            {busy
              ? t("login.loggingIn")
              : isBootstrap
                ? t("login.bootstrapTitle")
                : t("login.login")}
          </button>
        </form>
      </div>
    </div>
  );
}
